import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useGetInvestmentFundQuery } from 'api/main';
import { userSelectors } from 'reducers';
import { useCheckInvestmentFund } from 'utils/hooks';
import { fundTypes, investmentFunds } from 'utils/constants';
import { isStatusOk } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { SubmitButton, PDFViewer, Loader, CheckboxLabel } from 'components';
import TunnelStep from '../common/TunnelStep';
import TunnelBox from '../common/TunnelBox';
import { Box, Typography } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';
import { useSendRegulationAgreementMutation } from 'api/tunnelSteps';

const useStyles = makeStyles(theme => ({
    step11Box: {
        padding: '10px 0',
        backgroundColor: theme.palette.grey['A700'],
    },
    step11Url: {
        color: theme.palette.common.black,
        '&:hover': {
            color: theme.palette.primary.main,
        },
    },
    checkbox: {
        display: 'table',
        alignItems: 'baseline',

        '& .MuiFormControlLabel-label': {
            display: 'table-cell',
        },
    },
}));

const getDownloadRegulationText = (isSLP, isFPS) =>
    isSLP
        ? texts.DOWNLOAD_REGULATION_SLP
        : isFPS
        ? texts.DOWNLOAD_REGULATION_FPS
        : texts.DOWNLOAD_REGULATION;

const getTunnelStepTitle = (isSLP, isFPS) =>
    isSLP
        ? texts.FUND_REGULATIONS_SLP
        : isFPS
        ? texts.FUND_REGULATIONS_FPS
        : texts.FUND_REGULATIONS;

const getMustGoThroughRegulationText = (isSLP, isFPS) =>
    isSLP
        ? texts.MUST_GO_THROUGH_REGULATIONS_SLP
        : isFPS
        ? texts.MUST_GO_THROUGH_REGULATIONS_FPS
        : texts.MUST_GO_THROUGH_REGULATIONS;

const getAcceptanceOfRegulationsText = (isSLP, isFPS) =>
    isSLP
        ? texts.ACCEPTANCE_OF_REGULATIONS_SLP
        : isFPS
        ? texts.ACCEPTANCE_OF_REGULATIONS_FPS
        : texts.ACCEPTANCE_OF_REGULATIONS;

const FundRegulations = props => {
    const { handleNextStep, userStep } = props;

    const classes = useStyles();

    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const isApax = useCheckInvestmentFund(investmentFunds.APAX);
    const isSLP = tunnelSubscription?.investmentFund?.type === fundTypes.SLP;
    const isFPS = tunnelSubscription?.investmentFund?.type === fundTypes.FPS;

    const isResubmit = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);

    const [sendRegulationAgreement, { isLoading: buttonLoader, isSuccess }] =
        useSendRegulationAgreementMutation();
    const { data: investmentFund, isFetching: loading } = useGetInvestmentFundQuery(
        tunnelSubscription?.investmentFund?.id
    );

    const [regulationAgreement, setRegulationAgreement] = useState(
        isResubmit ? tunnelSubscription?.fundRegulationAgreement : false
    );
    const [disclaimer, setDisclaimer] = useState(
        isResubmit ? tunnelSubscription?.fundRegulationAgreement : false
    );
    const [reachedBottom, setReachBottom] = useState(
        isResubmit ? tunnelSubscription?.fundRegulationAgreement : false
    );

    const fundRegulationUrl = investmentFund?.fundRegulation?.url;

    const handleAgreementChange = () => setRegulationAgreement(prevState => !prevState);
    const handleDisclaimerChange = () => setDisclaimer(prevState => !prevState);

    const handleSubmit = useCallback(() => {
        sendRegulationAgreement(regulationAgreement);
    }, [sendRegulationAgreement, regulationAgreement]);

    useEffect(() => {
        if (isSuccess) {
            handleNextStep(true);
        }
    }, [isSuccess, handleNextStep]);

    return (
        <TunnelStep title={getTunnelStepTitle(isSLP, isFPS)} tunnelStepWidth={1100} maxWidth={1100}>
            <Box textAlign="center" p={1}>
                <Typography gutterBottom>{getMustGoThroughRegulationText(isSLP, isFPS)}</Typography>
            </Box>
            <TunnelBox
                sx={theme => ({ padding: '10px 0', backgroundColor: theme.palette.grey['A700'] })}
            >
                <Loader loading={loading}>
                    <PDFViewer url={fundRegulationUrl} setReachBottom={setReachBottom}></PDFViewer>
                </Loader>
            </TunnelBox>
            <Box textAlign="center" p={1}>
                <a
                    href={fundRegulationUrl}
                    download
                    target="_blank"
                    rel="noreferrer"
                    className={classes.step11Url}
                >
                    {getDownloadRegulationText(isSLP, isFPS)}
                </a>
            </Box>
            <CheckboxLabel
                className={classes.checkbox}
                onChange={handleAgreementChange}
                value={regulationAgreement}
                checked={regulationAgreement}
                label={getAcceptanceOfRegulationsText(isSLP, isFPS)}
            />
            {isApax && (
                <CheckboxLabel
                    className={classes.checkbox}
                    onChange={handleDisclaimerChange}
                    value={disclaimer}
                    checked={disclaimer}
                    label={texts.FUND_REGULATIONS_APAX}
                />
            )}
            <SubmitButton
                text={isResubmit ? texts.SUIVANT : texts.VALIDER}
                disabled={
                    !(regulationAgreement && reachedBottom && (isApax ? disclaimer : true)) ||
                    buttonLoader
                }
                onClick={hasStatusOk ? handleNextStep : handleSubmit}
                loading={buttonLoader}
            />
        </TunnelStep>
    );
};

export default FundRegulations;
